/**
 * An in-cell editor: the value, as a control you change where it is drawn.
 *
 * The owner, 17 Sep: "please make the fields into dropdowns, so that they can easily
 * be edited in place". Before this, every "inline" edit on the Tasks page was a Radix
 * dialog behind a cell that only looked like a field — click the cell, wait for a
 * window, choose, Save, wait for the window to go. Five steps to change one word.
 *
 * **Neither the members nor the words are written here.** The members are
 * `packages/shared`'s own closed sets — `TASK_QUEUES`, and the 1–5 that
 * `TaskPrioritySchema` is built from — so a seventh queue reaches the cell the day it
 * reaches the schema. The words are `taskWords.ts`, which `actionCopy` hands to the
 * generated form, so a cell and the create window read one vocabulary.
 *
 * The catalogue is deliberately **not** consulted. `ActionForm` reads it because it
 * draws a form for any action from a schema it has never seen; a cell knows which two
 * fields it holds, and a read that can fail would leave the column as plain text for
 * as long as it was failing.
 *
 * One component for the list and the board, for the same reason `facetsToTaskFilters`
 * is one function: the two surfaces must not be able to disagree about what a field
 * offers.
 *
 * Dialogs are not gone — a field that needs a form (money, the create window) still
 * opens one. This is for the closed sets a select can hold.
 */

import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type ReactNode } from 'react'

import {
  TASK_PRIORITY_MAX,
  TASK_PRIORITY_MIN,
  TASK_QUEUES,
  addDaysToDateOnly,
  dateOnlyOf,
  isDateOnly,
  daysInPeriod,
  firstDayOf,
  nextPeriod,
  periodOfDateOnly,
  previousPeriod,
  taskCanBeUnassigned,
  taskMachine,
  weekdayOf,
  type DateOnly,
  type Period,
  type TaskEvent,
  type TaskQueue,
  type TaskView,
} from '@fareast/shared'

import { messageOf } from '../../api/problem.js'
import { useFormat } from '../../format/useFormat.js'
import { now } from '../../integrations/clock.js'
import { ConfirmDialog } from '../portal/Overlay.js'
import { instantOf, localDateTimeOf } from '../portal/schemaForm.js'
import { Prose } from '../portal/Text.js'
import { afterLanded, isOnItsWay, useLandedWrites } from '../../api/hooks.js'
import { afterWindows, useEditing } from '../../shell/editing.js'
import { useStudioAction, useStudioRead, type StudioActionName } from '../../api/studio.js'
import { usePrincipal } from '../../auth/principal.js'
import { ActionDialog } from './ActionDialog.js'
import { NOBODY, assigneeValue } from './ActionField.js'
import { isOwnWriteUnder, ownMoveLanded, useOwnWriteOnItsWay } from './ActionForm.js'
import {
  AGENT_NOUN,
  DIALOG_MOVES,
  HELD_FIELDS,
  QUEUE_WORD,
  STATUS_WORD,
  canMutateWorkOf,
  heldAs,
  holdsUnmadeCall,
  isNewsSince,
  moveLabel,
  moveOf,
  moveWindowOf,
  overtaken,
  priorityWord,
  unassignQuestion,
  type HeldAs,
  type TaskMove,
} from './taskWords.js'

/** The `tasks_update` fields a cell can hold: a closed set, one select wide. */
export type CellField = 'priority' | 'queue'

type Choice = { readonly value: string; readonly label: string }

const PRIORITY_CHOICES: readonly Choice[] = Array.from(
  { length: TASK_PRIORITY_MAX - TASK_PRIORITY_MIN + 1 },
  (_unused, i) => {
    const band = TASK_PRIORITY_MIN + i
    return { value: String(band), label: priorityWord(band) }
  },
)

const QUEUE_CHOICES: readonly Choice[] = TASK_QUEUES.map((queue: TaskQueue) => ({
  value: queue,
  label: QUEUE_WORD[queue],
}))

const CHOICES: Readonly<Record<CellField, readonly Choice[]>> = {
  priority: PRIORITY_CHOICES,
  queue: QUEUE_CHOICES,
}

export type CellEditProps = {
  /** The task this edits. */
  readonly taskRef: string
  /** Which `tasks_update` field. */
  readonly field: CellField
  /** The value as the row carries it. Numbers arrive as numbers, queues as words. */
  readonly value: string | number
  /** What a screen reader hears — "Priority for TSK-0042". */
  readonly label: string
  /**
   * Told the old and new values after a save, so the page can offer Undo — and
   * `since`, the stamp the save's own answer carried (`stampOf`), which is what lets
   * that Undo be refused once the task has been written to again.
   */
  readonly onSaved?: (previous: string, next: string, since: string | null) => void
  /** Told why a save failed, so the page can say so where it says everything else. */
  readonly onError?: (message: string) => void
}

/**
 * The task's `updated_at` as a write's own answer carried it, or null when it carried
 * none.
 *
 * Every `tasks_*` write answers `{ task }`, the whole task, and `updated_at` moves on
 * every change to what the task says — a field, its status, who holds it; not an
 * agent's heartbeat, a comment or logged time — so it is the task's **version**, and
 * the one a write answers with is the version that write left behind. An Undo sends it back as
 * `if_unchanged_since` (`pages/studio/tasks/fieldSave.ts`), which is how the API
 * tells "put back what I just changed" from "write an old value over whatever
 * somebody has done since".
 *
 * Read off `unknown` rather than off the action's own output type, and null rather
 * than a guess: the answer may be a `needs_review` result, and a stamp nobody sent is
 * not one to make up — the page offers no way back without it.
 */
export function stampOf(answer: unknown): string | null {
  const stamp = (answer as { task?: { updated_at?: unknown } } | null | undefined)?.task?.updated_at
  return typeof stamp === 'string' ? stamp : null
}

/** Where a write's answer says the task stands now, or null when it does not say. */
export function statusOf(answer: unknown): TaskView['status'] | null {
  const status = (answer as { task?: { status?: unknown } } | null | undefined)?.task?.status
  return typeof status === 'string' && Object.hasOwn(STATUS_WORD, status)
    ? (status as TaskView['status'])
    : null
}

/**
 * True while a move of the reader's own on this task is on its way — a write that
 * says who holds it or where it stands, or a press of several (`ActionForm`
 * `useOwnWriteOnItsWay`). What every control that sends a move **at once** is shut
 * for: the Who cell, the Status cell and the row's buttons each kept their own flag,
 * so one sent from the row as it was drawn while another's move was still on its way.
 */
export function useOwnMoveOnItsWay(task: HeldAs & Pick<TaskView, 'ref'>): boolean {
  const move = moveOf(task)
  return useOwnWriteOnItsWay(move, move.if_was)
}

export function CellEdit({ taskRef, field, value, label, onSaved, onError }: CellEditProps) {
  const update = useStudioAction('tasks_update')
  /*
   * A write of the reader's own to this very field that has not landed: the Undo of
   * the value just picked, pressed and then picked over. Sent at once, the second
   * went out from what the row still showed and was refused for the reader's own
   * Undo, as somebody else's.
   */
  const coming = useOwnWriteOnItsWay({ task_ref: taskRef }, { [field]: value })
  /*
   * The select holds the chosen value until the answer arrives. A purely controlled
   * value would snap back to the row's old one for the length of the round trip,
   * which reads as the click having been refused.
   *
   * **And, saved, until the row under it is the row from after the save**
   * (`shell/editing.ts` `afterWindows`): the page behind an open window is held as it
   * was, and a save that lands there lands under a row still showing the old value.
   */
  const [pending, setPending] = useState<string | null>(null)
  const current = String(value)

  return (
    <select
      className="cell-edit"
      aria-label={label}
      value={pending ?? current}
      disabled={update.isPending || coming}
      onChange={(event) => {
        const next = event.target.value
        if (next === current) return
        setPending(next)
        // The promise, not per-call callbacks: a row the refresh filters out still reports.
        /*
         * The change, and what it is a change **from**: the value this cell was
         * showing. A row is up to a minute old, and without the second half a
         * priority picked from it went over one a colleague had set since, unseen.
         */
        const typed = (one: string): string | number => (field === 'priority' ? Number(one) : one)
        update
          .mutateAsync({
            task_ref: taskRef,
            [field]: typed(next),
            if_was: { [field]: typed(current) },
          })
          .then(
            (answer) => {
              afterWindows(() => {
                setPending(null)
              })
              onSaved?.(current, next, stampOf(answer))
            },
            (error: unknown) => {
              setPending(null)
              onError?.(overtaken(taskRef, error) ?? messageOf(error))
            },
          )
      }}
    >
      {CHOICES[field].map((one) => (
        <option key={one.value} value={one.value}>
          {one.label}
        </option>
      ))}
    </select>
  )
}

/* ------------------------------------------------------------------- who */

export type CellAssignProps = {
  readonly taskRef: string
  /** Where the task stands, so Unassigned asks first when somebody is on it (#48). */
  readonly status: TaskView['status']
  readonly kind: TaskView['assignee_kind']
  readonly id: string | null
  /** The holder's name as the row carries it — the list has no directory of its own. */
  readonly name: string | null
  readonly label: string
  /**
   * `released`: the save took somebody off live work, so it went back on the queue.
   * `since`: the stamp the save answered with, for the Undo (`stampOf`).
   */
  readonly onSaved?: (
    previous: AssigneePair,
    next: AssigneePair,
    released: boolean,
    since: string | null,
  ) => void
  readonly onError?: (message: string) => void
}

/** The pair `tasks_assign` takes, which the list also keeps for Undo. */
export type AssigneePair = {
  readonly kind: TaskView['assignee_kind']
  readonly id: string | null
}

/**
 * Who holds a task, as a select in the cell — the owner's "Click Who → assign or
 * reassign" without the window in between.
 *
 * The directory is `tasks_list_assignees`, the same read `ActionField`'s picker uses,
 * and the value is the same `"kind:id"` string, split the same way `ActionForm`
 * `bodyOf` splits it. `inline` so a directory that will not answer leaves a cell that
 * cannot be changed rather than an error card over the whole board.
 *
 * The holder is added to the list when the directory does not name them — an agent
 * that has since been retired still holds the task it holds, and a select that
 * silently showed somebody else would be the page lying about who has the work.
 */
export function CellAssign({
  taskRef,
  status,
  kind,
  id,
  name,
  label,
  onSaved,
  onError,
}: CellAssignProps) {
  const principal = usePrincipal()
  const assign = useStudioAction('tasks_assign')
  const people = useStudioRead('tasks_list_assignees', {}, { inline: true })
  const [pending, setPending] = useState<string | null>(null)
  /* A hand-over is made from who holds the task and where it stands: shut while either is moving. */
  const moving = useOwnMoveOnItsWay({
    ref: taskRef,
    assignee_kind: kind,
    assignee_id: id,
    status,
  })
  /* The question as it was asked, and about whom: a refetch must not close, re-arm or redirect it. */
  const [asking, setAsking] = useState<{
    readonly question: NonNullable<ReturnType<typeof unassignQuestion>>
    readonly holder: string
    readonly status: TaskView['status']
  } | null>(null)
  /*
   * Whether a move of the reader's own on this task has landed since the question was
   * asked — a batch that reached the row while they were deciding. The row under the
   * question is the row from before it (`shell/editing.ts`: the page behind a window
   * is still), so the row cannot say; the move's own answer does. Sent all the same,
   * the unassign went out from what the row had said and was refused for the reader's
   * own batch, as somebody else's.
   */
  const movedUnder = useRef(false)
  useLandedWrites((sent, answer) => {
    const asked = heldAs({ assignee_kind: kind, assignee_id: id, status })
    const landed = ownMoveLanded({ task_ref: taskRef }, asked, sent, answer)
    if (landed === null) return
    if (HELD_FIELDS.some((name) => landed.from[name] !== asked[name])) movedUnder.current = true
  }, asking !== null)

  const rows = people.data?.assignees ?? []
  const meId = principal.kind === 'staff_user' ? principal.userId : null
  const current = kind === 'none' || id === null ? NOBODY : assigneeValue(kind, id)
  const known = rows.some((one) => assigneeValue(one.kind, one.id) === current)

  const options: Choice[] = [
    // Not on a task that cannot be left with nobody (`taskCanBeUnassigned`): blocked,
    // it stays with whoever holds it, and the option was offered and refused every time.
    ...(taskCanBeUnassigned(status) || current === NOBODY
      ? [{ value: NOBODY, label: 'Unassigned' }]
      : []),
    ...(current !== NOBODY && !known
      ? [{ value: current, label: name ?? 'Whoever holds it' }]
      : []),
    ...rows.map((one) => ({
      value: assigneeValue(one.kind, one.id),
      label:
        one.kind === 'user' && one.id === meId
          ? 'Me'
          : one.kind === 'agent'
            ? `${one.name} — ${AGENT_NOUN}`
            : one.name,
    })),
  ]

  const commit = (next: string, asked: boolean): void => {
    setPending(next)
    const [nextKind = NOBODY, nextId] = next.split(':')
    // The promise, not per-call callbacks: a row the refresh filters out still reports.
    assign
      .mutateAsync({
        task_ref: taskRef,
        assignee_kind: nextKind,
        assignee_id: nextId ?? null,
        // Who the row says holds it, and where it stands: what this hand-over is made
        // from, so one made from an old row does not take a task off whoever has
        // picked it up since.
        if_was: heldAs({ assignee_kind: kind, assignee_id: id, status }),
      } as never)
      .then(
        (answer: unknown) => {
          // Kept while the row under it is the one from before (`CellEdit`).
          afterWindows(() => {
            setPending(null)
          })
          // Back on the queue only if the server says so: a task blocked meanwhile stays blocked.
          onSaved?.(
            { kind, id },
            { kind: nextKind as TaskView['assignee_kind'], id: nextId ?? null },
            asked && statusOf(answer) === 'open',
            stampOf(answer),
          )
        },
        (error: unknown) => {
          setPending(null)
          onError?.(overtaken(taskRef, error) ?? messageOf(error))
        },
      )
  }

  return (
    <>
      <select
        className="cell-edit"
        aria-label={label}
        value={pending ?? current}
        disabled={assign.isPending || people.isPending || moving}
        onChange={(event) => {
          const next = event.target.value
          if (next === current) return
          const question = next === NOBODY ? unassignQuestion([{ ref: taskRef, status }]) : null
          if (question !== null) {
            movedUnder.current = false
            setAsking({ question, holder: current, status })
            return
          }
          commit(next, false)
        }}
      >
        {options.map((one) => (
          <option key={one.value} value={one.value}>
            {one.label}
          </option>
        ))}
      </select>
      {asking === null ? null : (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setAsking(null)
          }}
          title={asking.question.title}
          confirmLabel={asking.question.confirmLabel}
          cancelLabel="Not yet"
          onConfirm={() => {
            setAsking(null)
            // By the row, where it has been drawn again; by the reader's own move, where
            // it has not (`movedUnder`) or is still on its way (`moving`).
            if (
              movedUnder.current ||
              moving ||
              asking.holder !== current ||
              asking.status !== status
            ) {
              onError?.(`${taskRef} changed while you were deciding. Nothing was changed.`)
              return
            }
            commit(NOBODY, true)
          }}
        >
          <Prose>{asking.question.says}</Prose>
        </ConfirmDialog>
      )}
    </>
  )
}

/* ------------------------------------------------------------- due date */

export type CellDueProps = {
  readonly taskRef: string
  /** The instant the row carries, or null for a task with no date. */
  readonly value: string | null
  /**
   * The stamp (`updated_at`) of the row `value` was read from: what tells an answer of
   * the reader's own that is news to a draft from one the row had already shown.
   */
  readonly stamp: string
  /**
   * The urgency line for a due date — colour, mark and "· 28 d late". Drawn, never
   * replaced.
   *
   * A function of the date rather than a node, because the date it is drawn for is
   * not always the row's: while a save is on its way the cell reads the date that
   * was chosen (`pending` below). The reading still has one owner — `TaskWhen`, at
   * both call sites — whichever of the two dates it is handed.
   */
  readonly display: (dueAt: string | null) => ReactNode
  readonly label: string
  /*
   * `| undefined` spelled out, because `exactOptionalPropertyTypes` is on and a
   * caller that forwards its own optional prop (`TaskCardFull`) is handing over the
   * absence as a value rather than leaving the key off.
   *
   * `since` is the stamp the save answered with, for the Undo (`stampOf`).
   */
  readonly onSaved?:
    ((previous: string | null, next: string | null, since: string | null) => void) | undefined
  readonly onError?: ((message: string) => void) | undefined
}

/**
 * A due date changed without leaving the row.
 *
 * A real `datetime-local` lies over the cell, transparent until it has focus, so one
 * click lands in the field and opens the calendar. The reading underneath is
 * `TaskWhen`'s — colour, mark and "· 28 d late", the owner's item 14 — which a bare
 * date box would have thrown away, so it shows through and is `aria-hidden`.
 *
 * **Nothing is saved while you type.** `datetime-local` fires `change` on every
 * segment, so saving there meant pressing "1" on the way to 12 wrote the first of
 * the two and the re-render ate the rest. The box holds a draft; it commits when
 * focus leaves it or on Enter — by one rule, `touched`: a box nobody changed writes
 * nothing, whichever of the two ends the visit — and Escape puts the old value back.
 * The row's Undo line covers what does get written.
 *
 * **The draft changes two ways and no third**: a date typed in the box, and a day
 * picked in the grid. The grid's arrows turn its page and nothing else (`viewing`);
 * they used to move the draft a month at a time, so looking ahead and clicking away
 * saved a date nobody had chosen.
 *
 * **A box that does not hold a date saves nothing, and says so.** A `datetime-local`
 * whose time segment is empty reports its value as `''` — the same thing it reports
 * when the field has been emptied on purpose — so a date typed on its own used to
 * commit `due_at: null` and the row still said "updated": the date the reader had
 * just chosen was gone and the page called it a save. Now a half-typed box
 * (`badInput`) commits nothing and tells `onError` why — the cell snapping back to
 * the old date with no sentence read as a save that had been lost — a box emptied on
 * purpose takes the date off and says so ("removed"), and a date with no time means
 * `DEFAULT_DUE_TIME` on that day. Which is also why an empty box **opens** on that
 * hour: with the time already filled in, the date is the only segment left to type,
 * and what the box holds is a value the browser will hand back.
 *
 * **The chosen date stays on screen while it is saved** (`pending`), in the box and
 * in the reading under it: the row's own value is the old one until the write and
 * the re-read it waits for have both come back, and the old date drawn for that long
 * reads as "it did not take".
 */
export function CellDue({ taskRef, value, stamp, display, label, onSaved, onError }: CellDueProps) {
  const queryClient = useQueryClient()
  const update = useStudioAction('tasks_update')
  const format = useFormat()
  const asBox = value === null ? '' : localDateTimeOf(value)
  const [draft, setDraft] = useState<string | null>(null)
  /*
   * The date on its way, until the answer arrives — `CellEdit`'s `pending`, for
   * `CellEdit`'s reason. Wrapped, because taking the date off is a save too and its
   * value is `null`, which bare would be "nothing is on its way".
   */
  const [pending, setPending] = useState<{ readonly dueAt: string | null } | null>(null)
  /*
   * The month the grid shows once the reader has paged away from the draft's own, or
   * null while it follows the draft — which is what it does from the moment the cell
   * opens, and again as soon as a date is typed or a day is picked.
   */
  const [viewing, setViewing] = useState<Period | null>(null)
  /*
   * Escape has to survive the blur it causes. Clearing the draft is not enough: the
   * box is still holding the typed text at the moment `blur` fires, and `blur`
   * commits what the box holds — so pressing Escape used to **save** the very change
   * it was cancelling. A ref rather than state, because the blur happens in the same
   * turn and would read a stale value.
   */
  const cancelled = useRef(false)
  /*
   * Whether anybody actually changed anything. An empty box is opened on the default
   * hour so that a typed date is a whole value, and a reader who clicks a cell and
   * then clicks away has chosen nothing — without this, that click would save the
   * hour the box was seeded with.
   */
  const touched = useRef(false)
  /*
   * The date the draft was started over, which is what a save is made **from**.
   *
   * Not the row's date when the save is sent. The box keeps a draft through a
   * re-read — the reader's own save of another row brings one — and the reading
   * under a box with the cursor in it is hidden, so a date a colleague set meanwhile
   * is one the reader has not been shown: sent as the condition, it passed, and
   * their date was written over by somebody who never saw it. Taken when the draft
   * begins rather than when the cursor arrives, because until then the box is
   * showing the row and follows it.
   */
  const seen = useRef(value)
  /** The stamp of the row `seen` was read from, or of the answer that amended it. */
  const seenAt = useRef(stamp)
  /** A draft begins: over the row as it is drawn now. */
  const begin = (): void => {
    seen.current = value
    seenAt.current = stamp
  }
  /*
   * A date of the reader's **own** that lands under the draft is the date the draft
   * is now over — the Undo of the one just saved is the usual one, retyped before the
   * Undo had come back. From that write's own answer and not from the row
   * (`api/hooks.ts` `useLandedWrites`): the row says what the date *is*, which is a
   * colleague's as readily as the reader's, and theirs must stay a refusal.
   *
   * **And only where that answer is news** (`taskWords.ts` `isNewsSince`): a draft
   * begun over a row that had already been read again after that write — and showed a
   * date a colleague set later still — has seen more than the answer says. Which also
   * makes hearing one answer twice the same as hearing it once.
   */
  const amend = (sent: unknown, answer: unknown): void => {
    // Whether it is the reader's own, and about this date: the one reading there is.
    const landed = ownMoveLanded({ task_ref: taskRef }, { due_at: seen.current }, sent, answer)
    if (landed === null || !isNewsSince(seenAt.current, landed.stamp)) return
    const dueAt = landed.from['due_at']
    if (typeof dueAt !== 'string' && dueAt !== null) return
    seen.current = dueAt
    if (landed.stamp !== undefined) seenAt.current = landed.stamp
  }
  useLandedWrites(amend)

  const commit = (raw: string, refused: boolean): void => {
    setDraft(null)
    touched.current = false
    /*
     * Nothing saved — and **said**, because this is only reached for a box somebody
     * changed (`touched`). `refused` is the browser's own verdict — a half-typed box
     * is `badInput` — and `dueInstantOf` answers null for everything else a box can
     * hold that is not a date. Saving nothing is right for both; saying nothing left
     * the cell back on its old date with no word about the one just typed.
     */
    if (refused) {
      onError?.(notADate(taskRef))
      return
    }
    // An emptied box takes the date off; anything else that is not a date saves nothing.
    const cleared = raw.trim() === ''
    const next = cleared ? null : dueInstantOf(raw)
    if (!cleared && next === null) {
      onError?.(notADate(taskRef))
      return
    }
    /*
     * The save. The promise, not per-call callbacks: a row the refresh filters out
     * still reports.
     *
     * From the date the draft is over (`if_was`, `seen`): a date somebody else has
     * moved since is theirs, and is not written over by a reader who was not shown it.
     * **And that date is what the save replaced** — the API has just agreed it was the
     * date — so it is what the way back puts back (`onSaved`), not the row as the
     * cursor left it, which a write of the reader's own may since have changed.
     */
    const save = (): void => {
      const from = seen.current
      update.mutateAsync({ task_ref: taskRef, due_at: next, if_was: { due_at: from } }).then(
        (answer) => {
          // Kept while the row under it is the one from before (`CellEdit`).
          afterWindows(() => {
            setPending(null)
          })
          onSaved?.(from, next, stampOf(answer))
        },
        (error: unknown) => {
          // Back to the row's own date; why is the page's line to say.
          setPending(null)
          onError?.(overtaken(taskRef, error) ?? messageOf(error))
        },
      )
    }
    const about = { task_ref: taskRef }
    const stillComing = (sent: unknown): boolean =>
      isOwnWriteUnder(about, { due_at: seen.current }, sent)
    if (!isOnItsWay(queryClient, stillComing)) {
      // Left as it was: not a save, and not a thing to announce either.
      if (next === value) return
      setPending({ dueAt: next })
      // In this turn, as the cursor leaves (`api/hooks.ts` `isOnItsWay`).
      save()
      return
    }
    /*
     * **A write of the reader's own on this date is still on its way**, and the save
     * waits for it, as a window and a batch wait (`ActionForm` `landing`, `BulkBar`
     * `runBulk`): the Undo of the date just saved, pressed and then retyped over and
     * left before it had come back. `seen` is amended when that Undo lands (below) —
     * which is no use to a save that went out before it, and was refused for the
     * reader's own Undo as somebody else's, with the typed date gone.
     *
     * Whether there is anything to save is asked **then**, of what that write left:
     * the row as the cursor left it is the one thing known to be about to change, and
     * a date retyped to what the row still said was dropped without a word while the
     * Undo went on to take it off.
     *
     * **The wait hears what lands by itself** (`amend`, handed to it): the listener
     * above goes with the cell, and a cell left by changing the view is gone before
     * that write lands — its save then went out from the date the write had just
     * replaced. **And it asks again in the turn it sends** (`afterLanded`): a batch
     * standing at this row was waiting for the same write.
     */
    setPending({ dueAt: next })
    void afterLanded(
      queryClient,
      stillComing,
      () => {
        if (next === seen.current) {
          setPending(null)
          return
        }
        save()
      },
      amend,
    )
  }

  /*
   * The panel is open while the field has focus, and **nothing takes that focus
   * away**: every mousedown inside it is prevented, so typing continues while the
   * grid is on screen and the grid follows what is typed.
   */
  const [open, setOpen] = useState(false)
  /*
   * A cell with the cursor in it holds the page's live reads still, as an open window
   * does (`shell/editing.ts`): the draft lives in this row, and a refresh that sorted
   * the row off the loaded page took the half-typed date with it.
   */
  useEditing(open)
  /*
   * What ends a visit to the cell: the draft is saved if somebody changed it, and
   * dropped if they did not or pressed Escape. `blur` is the usual caller.
   */
  const leave = (input: HTMLInputElement): void => {
    setOpen(false)
    if (cancelled.current) {
      cancelled.current = false
      touched.current = false
      setDraft(null)
      return
    }
    if (!touched.current) {
      setDraft(null)
      return
    }
    commit(input.value, refusedBy(input))
  }
  /*
   * **The cursor can leave without saying so.** A save elsewhere on the page re-reads
   * the list, and under a sort by date that can move this very row; a browser takes
   * the focus off a field whose row has been moved, and not every browser fires
   * `blur` when it does. `open` is this cell's whole idea of where the cursor is, and
   * it holds the page's live reads still — so with no blur the board went on being
   * held by a cell nobody was in, refreshing for nothing but the reader's own saves.
   *
   * So while the cell is open it asks, at the next thing the reader does anywhere,
   * whether the box still has the cursor; if it has not, the visit ends as a blur
   * would have ended it. The calendar under the box is the cell's own and is left
   * alone, and a second ending is harmless: `leave` reads `touched`, which the first
   * one cleared.
   */
  const box = useRef<HTMLInputElement>(null)
  const ended = useRef(leave)
  ended.current = leave
  useEffect(() => {
    if (!open) return
    const check = (event: Event): void => {
      const input = box.current
      if (input === null || document.activeElement === input) return
      if (event.target instanceof Node && input.parentElement?.contains(event.target) === true)
        return
      ended.current(input)
    }
    document.addEventListener('focusin', check, true)
    document.addEventListener('pointerdown', check, true)
    return () => {
      document.removeEventListener('focusin', check, true)
      document.removeEventListener('pointerdown', check, true)
    }
  }, [open])
  /* What the row holds, or what is on its way to it: never the old date over a save. */
  const dueAt = pending === null ? value : pending.dueAt
  const shown = draft ?? (pending === null ? asBox : dueAt === null ? '' : localDateTimeOf(dueAt))
  /*
   * A half-typed date is not a date.
   *
   * Typing a year takes four keystrokes and the box holds `0202-09-18T17:00` on the
   * way there, which `DATE_ONLY_PATTERN` refuses — and refusing it **threw**, which
   * took the page down mid-edit. So the grid falls back: the typed day if it is one,
   * else the day the row already carries, else today. The box keeps whatever is
   * being typed; only the calendar beside it waits for a date that exists.
   */
  const day = firstValidDay([shown.slice(0, 10), asBox.slice(0, 10)])
  const rawTime = shown.slice(11)
  const time = /^\d{2}:\d{2}/.test(rawTime) ? rawTime : DEFAULT_DUE_TIME
  /* The draft's own month, unless the arrows have turned the page away from it. */
  const month = viewing ?? periodOfDateOnly(day)
  const first = firstDayOf(month)
  const lead = (weekdayOf(first) + 6) % 7
  const days = daysInPeriod(month)

  const pick = (chosen: DateOnly): void => {
    if (draft === null) begin()
    touched.current = true
    // The day is in the month on screen, so following the draft again moves nothing.
    setViewing(null)
    setDraft(`${chosen}T${time}`)
  }

  return (
    <span className="cell-when">
      <span className="cell-when__read" aria-hidden="true">
        {display(dueAt)}
      </span>
      <input
        ref={box}
        className="cell-when__box"
        type="datetime-local"
        aria-label={label}
        /*
         * Four digits of year, not six. A bare `datetime-local` lets the year segment
         * run to 275760, and a task due in the year 20261 is a typo nobody meant.
         */
        min="1000-01-01T00:00"
        max="9999-12-31T23:59"
        value={shown}
        // From the moment a date is on its way: the wait for the reader's own write, and the save.
        disabled={pending !== null}
        onFocus={() => {
          setOpen(true)
          // On the month of the date the cell holds, whatever was being looked at last time.
          setViewing(null)
          if (asBox === '' && draft === null) {
            begin()
            setDraft(`${todayInBox()}T${DEFAULT_DUE_TIME}`)
          }
        }}
        onChange={(event) => {
          if (draft === null) begin()
          touched.current = true
          // The grid follows what is typed, so a date in another month turns its page.
          setViewing(null)
          setDraft(event.target.value)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault()
            cancelled.current = true
            touched.current = false
            setDraft(null)
            setOpen(false)
            event.currentTarget.value = asBox
            event.currentTarget.blur()
          }
          if (event.key === 'Enter') {
            event.preventDefault()
            setOpen(false)
            /*
             * Blur's rule, to the letter: nothing touched, nothing written. An empty
             * cell is holding today at the default hour from the moment it has focus
             * (`onFocus`), so Tab and Enter used to **save today's date** on a task
             * nobody had dated. The seed stays in the box because the cursor does;
             * the blur that ends the visit is what takes it away.
             */
            if (touched.current) commit(event.currentTarget.value, refusedBy(event.currentTarget))
          }
        }}
        onBlur={(event) => {
          leave(event.currentTarget)
        }}
      />

      {!open ? null : (
        <span
          className="datepick"
          /*
           * The whole panel refuses focus. A calendar that closed the moment it was
           * clicked, or that took the cursor out of the box, would be the thing this
           * was built to stop.
           */
          onMouseDown={(event) => {
            event.preventDefault()
          }}
        >
          {/*
            The arrows turn the page, and that is all they do.

            Each used to `pick` the same day of the month it moved to: the draft was
            rewritten, `touched` was set, and the blur that followed committed the
            box. So opening a cell, pressing › twice to see what October looks like
            and clicking away wrote a due date two months later — said on the strip
            and undoable, and still a date nobody chose. Which month is on screen is
            the grid's own (`viewing`); only a day in it, or the box, is a date.
          */}
          <span className="datepick__nav">
            <button
              type="button"
              aria-label="The month before"
              onClick={() => {
                setViewing(previousPeriod(month))
              }}
            >
              ‹
            </button>
            {/*
              The month a person reads, not the key the code holds. This printed the
              `Period` itself — "2026-08" over a grid of days — and `useFormat`'s
              `monthLong` is the portal's own spelling of it, the same one a report
              title uses. CONTRACT.md's rule that every date goes through
              `useFormat()` was never about the grid; the heading is a date too.
            */}
            <b>{format.monthLong(month)}</b>
            <button
              type="button"
              aria-label="The month after"
              onClick={() => {
                setViewing(nextPeriod(month))
              }}
            >
              ›
            </button>
          </span>
          <span className="datepick__grid">
            {WEEKDAYS.map((name) => (
              <span className="datepick__dow" key={name}>
                {name}
              </span>
            ))}
            {Array.from({ length: lead }, (_unused, i) => (
              <span key={`lead-${String(i)}`} />
            ))}
            {Array.from({ length: days }, (_unused, i) => {
              const each = addDaysToDateOnly(first, i)
              return (
                <button
                  type="button"
                  key={each}
                  className="datepick__day"
                  aria-current={each === day ? 'date' : undefined}
                  aria-label={each}
                  onClick={() => {
                    pick(each)
                  }}
                >
                  {i + 1}
                </button>
              )
            })}
          </span>
        </span>
      )}
    </span>
  )
}

/**
 * The hour a date with no time of its own means: the end of the working day.
 *
 * Six in the evening rather than midnight, because a task due "on Friday" is due by
 * the end of Friday, and midnight would draw every such task as due on Thursday
 * night and late for the whole of the day it is due.
 */
const DEFAULT_DUE_TIME = '18:00'

const DATE_ONLY_BOX = /^\d{4}-\d{2}-\d{2}$/
const DATE_TIME_BOX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/

/**
 * What a due-date box means as an instant, or **null** when it means nothing.
 *
 * Null is the whole point: `due_at: null` is a real edit — it takes the date off the
 * task — and it must never be the accidental reading of a box somebody was still
 * typing in. So only two shapes are a date here: a whole local date and time, and a
 * bare day, which is that day at `DEFAULT_DUE_TIME` in the reader's own zone.
 * Everything else — empty, half a year, a day that does not exist — is nothing.
 */
export function dueInstantOf(raw: string): string | null {
  const text = raw.trim()
  const whole = DATE_ONLY_BOX.test(text) ? `${text}T${DEFAULT_DUE_TIME}` : text
  if (!DATE_TIME_BOX.test(whole)) return null
  /* A year on the way to being typed: `0202-09-18T17:00` is a date and is not one. */
  if (Number(whole.slice(0, 4)) < 1000) return null
  const instant = instantOf(whole)
  /* `instantOf` hands back what it was given when the platform cannot read it. */
  if (instant === whole) return null
  /*
   * A day the calendar does not have **rolls over** rather than refusing: the
   * platform reads 31 February as 3 March, so a box that says one thing would be
   * saved as another. Only the day is compared — an hour that does not exist on a
   * clock-change morning is a real instant and is saved as the one the reader meant.
   */
  if (localDateTimeOf(instant).slice(0, 10) !== whole.slice(0, 10)) return null
  return instant
}

/**
 * The browser's own verdict on a box nobody finished filling in.
 *
 * `badInput` is what a `datetime-local` says about a segment left empty, and it is
 * the only way to tell that apart from a box somebody emptied on purpose: both
 * report their value as `''`. The two range flags are the `min`/`max` on the field —
 * the four-digit year — which a half-typed year trips on the way past.
 */
function refusedBy(input: HTMLInputElement): boolean {
  const state = input.validity
  return state.badInput || state.rangeUnderflow || state.rangeOverflow
}

/**
 * What the page is told when a box somebody changed holds no date to save.
 *
 * It names the task because the sentence is read on the page's one line, under forty
 * rows, and it says what did **not** happen because that is the fact: the cell is
 * back on the date it had, and without this nothing said the typed one was dropped.
 */
function notADate(taskRef: string): string {
  return `That is not a whole date and time, so the due date of ${taskRef} was not changed.`
}

/** Today, as a `datetime-local` box spells its date half, in the reader's zone. */
function todayInBox(): string {
  return localDateTimeOf(now().toISOString()).slice(0, 10)
}

/** The first of these that is a real date, or today. Never throws. */
function firstValidDay(candidates: readonly string[]): DateOnly {
  for (const each of candidates) {
    if (isDateOnly(each)) return each
  }
  return dateOnlyOf(now(), 'UTC')
}

/** Monday first, which is what `en-GB` reads. */
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const

/* ---------------------------------------------------------------- status */

export type CellStatusProps = {
  readonly task: TaskView
  /** Who is reading — the machine's moves are narrowed by who owns the work. */
  readonly viewerId: string | null
  /**
   * Told which move was made, and where the answer says the task stands after it
   * (null when it does not say), so the page can say so on its one line.
   *
   * A prop for the reason `onError` is one: the sentence is the page's
   * (`pages/studio/tasks/fieldSave.ts` `movedLabel`), and a cell that reached into a
   * page for it could be drawn nowhere else. `landed` rides along because one move
   * has two endings — `complete` on a task that needs a person goes to review — and
   * only the answer knows which.
   */
  readonly onMoved?: (move: TaskEvent, landed: TaskView['status'] | null) => void
  readonly onError?: (message: string) => void
}

/**
 * A move the select runs on its own: it asks the reader nothing, it takes nothing
 * from anybody, and it tells nobody.
 *
 * `release` and `submit_for_review` used to be here on the first of those grounds
 * alone, and fail the other two: one takes live work off whoever holds it and leaves
 * the task open and unassigned, the other mails the reviewer, and neither can be
 * undone. One mis-click in a select, with no question before it and no sentence
 * after. They open their `DIALOG_MOVES` window now, as they do from the row's More
 * menu and on the task page.
 */
const BARE_MOVES: Readonly<Partial<Record<TaskEvent, StudioActionName>>> = {
  claim: 'tasks_claim',
  start: 'tasks_start',
  unblock: 'tasks_unblock',
}

/**
 * The status, in the cell, offering only the moves the machine allows (#107).
 *
 * The status is **not** an ordinary field and this is not an ordinary dropdown: a
 * free select over `TASK_STATUSES` would offer `open → done`, which
 * `taskMachine.transition` refuses, and writing a status anywhere but through the
 * machine is the thing `.docs/00-index.md`'s registry forbids outright. So the
 * options are `taskMachine.eventsFrom(status)` narrowed by `canMutateWorkOf`, and
 * each one runs its own existing action.
 *
 * **Three run on the spot** — `claim`, `start` and `unblock` (`BARE_MOVES`): two
 * clicks, which is the budget, and the budget is affordable for exactly these because
 * each moves the reader's own task forward and nothing else. **The rest open the
 * dialog they already have**: `complete` wants an outcome and `block` a reason, both
 * `min(3)`, which the row has nowhere to type; `release` and `submit_for_review` want
 * neither, and get the window for what they *do* — see `BARE_MOVES`.
 *
 * **Every move says what it did**, through `onMoved`, whichever of the two ways it
 * ran. The select rests on the status word, so a move made by mistake used to leave
 * a row that had quietly become something else and nothing on the page to say which
 * row or why. No Undo is offered here: a move is taken back by another move, and
 * the ones there are from where the task now stands are what the cell offers next.
 */
export function CellStatus({ task, viewerId, onMoved, onError }: CellStatusProps) {
  const [pending, setPending] = useState<TaskEvent | null>(null)
  /* The move a window is open for. The event, not its words: the report names the move. */
  const [asking, setAsking] = useState<TaskEvent | null>(null)
  const claim = useStudioAction('tasks_claim')
  const start = useStudioAction('tasks_start')
  const unblock = useStudioAction('tasks_unblock')
  const runners: Readonly<
    Record<
      string,
      {
        mutateAsync: (input: { task_ref: string; if_was?: HeldAs }) => Promise<unknown>
        isPending: boolean
      }
    >
  > = {
    tasks_claim: claim,
    tasks_start: start,
    tasks_unblock: unblock,
  }
  const busy = Object.values(runners).some((one) => one.isPending)
  /* A move is made from who holds the task and where it stands: shut while either is moving. */
  const moving = useOwnMoveOnItsWay(task)
  const dialog: TaskMove | undefined = asking === null ? undefined : DIALOG_MOVES[asking]

  const mayWork = canMutateWorkOf(task, viewerId)
  /* A call nobody has made is read and made on the task's own page, never closed from a row. */
  const unmade = holdsUnmadeCall(task)
  const moves = taskMachine
    .eventsFrom(task.status)
    .filter((event) => mayWork || event === 'approve_review')
    .filter((event) => !(unmade && event === 'approve_review'))
    /*
     * Cancel is **not** here, and that is the one exception to "the machine decides".
     * It is in the More menu on every row, card and page, where it is red and asks
     * which task; a second copy in a list of ordinary moves is a destructive move one
     * scroll of a select away from Start — and the cell offered it even to somebody
     * the ownership rule refuses every other move to.
     */
    .filter((event) => event !== 'cancel')
    .filter((event) => BARE_MOVES[event] !== undefined || DIALOG_MOVES[event] !== undefined)

  return (
    <>
      <select
        className="cell-edit"
        aria-label={`Status of ${task.ref}`}
        value=""
        disabled={busy || moving || moves.length === 0}
        onChange={(event) => {
          const move = event.target.value as TaskEvent
          const bare = BARE_MOVES[move]
          if (bare !== undefined) {
            setPending(move)
            // The promise, not per-call callbacks: a row the refresh filters out still reports.
            // A claim says nothing of what it is made from — it is refused by itself on a
            // task that is somebody else's — and the other two say what the row said.
            runners[bare]
              ?.mutateAsync(move === 'claim' ? { task_ref: task.ref } : moveOf(task))
              .then(
                (answer) => {
                  setPending(null)
                  onMoved?.(move, statusOf(answer))
                },
                (error: unknown) => {
                  setPending(null)
                  onError?.(overtaken(task.ref, error, 'move') ?? messageOf(error))
                },
              )
            return
          }
          if (DIALOG_MOVES[move] !== undefined) setAsking(move)
        }}
      >
        {/* The current status is the resting label, and choosing it is not a move. */}
        <option value="">{pending === null ? STATUS_WORD[task.status] : 'Working…'}</option>
        {moves.map((event) => (
          <option key={event} value={event}>
            {moveLabel(event)}
          </option>
        ))}
      </select>

      {asking === null || dialog === undefined ? null : (
        <ActionDialog
          action={dialog.action}
          label={dialog.label}
          title={dialog.title}
          /* Which task the window is about — the row behind it is one of forty. */
          about={
            <>
              {task.ref} · {task.title}
            </>
          }
          submitLabel={dialog.label}
          size="sm"
          {...moveWindowOf(task)}
          open
          onOpenChange={(next) => {
            if (!next) setAsking(null)
          }}
          /*
           * The window's own answer is reported the way a move made on the spot is:
           * one cell, one convention. It closes over a row that may have left the
           * list by now — a task marked done is not live — and `onMoved` is the
           * page's, so the sentence is still said.
           */
          onDone={(output) => {
            setAsking(null)
            onMoved?.(asking, statusOf(output))
          }}
        />
      )}
    </>
  )
}
